/**
 * Moment.js Date Formatting Extensions
 * 
 * This file provides locale-aware date formatting extensions for moment.js
 * Supports en-US and en-GB locales with consistent formatting patterns
 * 
 * Usage:
 * import { MomentExtensions, moment } from "./moment-extensions";
 * MomentExtensions.setLocale("en-GB");
 * moment().toShortDate(); // Uses locale-aware formatting
 */

import moment from 'moment';

// Locale-aware format configurations (GB and US only)
const MOMENT_LOCALE_FORMATS = {
    'en-US': {
        shortDate: 'MM/DD/YYYY',           // 01/15/2024
        mediumDate: 'MMM D, YYYY',         // Jan 15, 2024
        longDate: 'MMMM D, YYYY',          // January 15, 2024
        shortTime: 'h:mm A',               // 3:30 PM
        mediumTime: 'h:mm:ss A',           // 3:30:45 PM
        shortDateTime: 'MMM D, YYYY h:mm A',      // Jan 15, 2024 3:30 PM
        mediumDateTime: 'MMMM D, YYYY h:mm A',    // January 15, 2024 3:30 PM
        longDateTime: 'MMM D, YYYY, h:mm A',      // Jan 15, 2024, 3:30 PM (with comma)
        chartLabel: 'MMM DD',              // Jan 15
        monthYear: 'MMMM YYYY',            // January 2024
        monthYearShort: 'MMM YYYY',        // Jan 2024
        apiDate: 'YYYY-MM-DD',             // 2024-01-15
        apiDateTime: 'YYYY-MM-DD HH:mm:ss', // 2024-01-15 15:30:00
        localeOptions: {
            shortDateTime: { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
            mediumDateTime: { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
            longDateTime: { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
            timeOnly: { hour: 'numeric', minute: '2-digit', hour12: true }
        },
        weekStart: 0 // Sunday
    },
    'en-GB': {
        shortDate: 'DD/MM/YYYY',           // 15/01/2024
        mediumDate: 'D MMM YYYY',          // 15 Jan 2024
        longDate: 'D MMMM YYYY',           // 15 January 2024
        shortTime: 'h:mm A',                // 3:30 PM
        mediumTime: 'h:mm:ss A',           // 3:30:45 PM
        shortDateTime: 'D MMM YYYY h:mm A',      // 15 Jan 2024 3:30 PM
        mediumDateTime: 'D MMMM YYYY h:mm A',    // 15 January 2024 3:30 PM
        longDateTime: 'D MMM YYYY, h:mm A',      // 15 Jan 2024, 3:30 PM (with comma)
        chartLabel: 'DD MMM',              // 15 Jan
        monthYear: 'MMMM YYYY',            // January 2024
        monthYearShort: 'MMM YYYY',        // Jan 2024
        apiDate: 'YYYY-MM-DD',             // 2024-01-15
        apiDateTime: 'YYYY-MM-DD HH:mm:ss', // 2024-01-15 15:30:00
        localeOptions: {
            shortDateTime: { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
            mediumDateTime: { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
            longDateTime: { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
            timeOnly: { hour: 'numeric', minute: '2-digit', hour12: true }
        },
        weekStart: 1 // Monday
    }
};

const DEFAULT_LOCALE = 'en-US';
let currentMomentLocale = DEFAULT_LOCALE;

/**
 * Moment.js Locale-Aware Extensions Manager
 * Provides consistent, locale-aware date formatting throughout the application
 */
class MomentExtensions {
    
    /**
     * Set the current locale for all moment operations
     * @param {string} locale - Locale code (en-US, en-GB)
     */
    static setLocale(locale) {
        if (MOMENT_LOCALE_FORMATS[locale]) {
            currentMomentLocale = locale;
            const config = MOMENT_LOCALE_FORMATS[locale];

            // Update moment's locale settings for week start
            moment.updateLocale('en', {
                week: {
                    dow: config.weekStart
                }
            });
        } else {
            currentMomentLocale = DEFAULT_LOCALE;
        }
    }
    
    /**
     * Get current locale
     * @returns {string} Current locale code
     */
    static getCurrentLocale() {
        return currentMomentLocale;
    }
    
    /**
     * Get locale format configuration
     * @param {string} locale - Optional locale override
     * @returns {object} Locale format configuration
     */
    static getLocaleFormats(locale = null) {
        return MOMENT_LOCALE_FORMATS[locale || currentMomentLocale] || MOMENT_LOCALE_FORMATS[DEFAULT_LOCALE];
    }
    
    /**
     * Get toLocaleString options for a specific format type
     * @param {string} type - Format type (shortDateTime, mediumDateTime, etc.)
     * @param {string} locale - Optional locale override
     * @returns {object} Options object for toLocaleString
     */
    static getLocaleStringOptions(type, locale = null) {
        const formats = this.getLocaleFormats(locale);
        return formats.localeOptions[type] || {};
    }
    
    // Utility static methods
    static now() { return moment(); }
    static parse(date) { return moment(date); }
    static parseTimestamp(timestamp) { return moment(timestamp * 1000); }

    /**
     * Parse date string using default locale for consistent API operations
     * @param {string} dateString - Date string to parse
     * @param {string|null} format - Optional format string
     * @returns {moment.Moment} Moment instance parsed with default locale
     */
    static parseForApi(dateString, format = null) {
        if (currentMomentLocale === DEFAULT_LOCALE) {
            return moment(dateString, format);
        }
        // Temporarily switch to the default locale for parsing
        const originalLocale = currentMomentLocale;
        this.setLocale(DEFAULT_LOCALE);
        const result = moment(dateString, format || 'YYYY-MM-DD HH:mm:ss');
        this.setLocale(originalLocale);
        return result;
    }
}

// ============================================================================
// MOMENT.JS PROTOTYPE EXTENSIONS
// ============================================================================

// Basic Date Formatting Methods
moment.fn.toShortDate = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).shortDate;
    return this.format(format);
};

moment.fn.toMediumDate = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).mediumDate;
    return this.format(format);
};

moment.fn.toLongDate = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).longDate;
    return this.format(format);
};

// Time Formatting Methods
moment.fn.toShortTime = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).shortTime;
    return this.format(format);
};

moment.fn.toMediumTime = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).mediumTime;
    return this.format(format);
};

// DateTime Combination Methods
moment.fn.toShortDateTime = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).shortDateTime;
    return this.format(format);
};

moment.fn.toMediumDateTime = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).mediumDateTime;
    return this.format(format);
};

moment.fn.toLongDateTime = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).longDateTime;
    return this.format(format);
};

// Display/Chart Formatting Methods
moment.fn.toChartLabel = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).chartLabel;
    return this.format(format);
};

moment.fn.toMonthYear = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).monthYear;
    return this.format(format);
};

moment.fn.toMonthYearShort = function(locale = null) {
    const format = MomentExtensions.getLocaleFormats(locale).monthYearShort;
    return this.format(format);
};

// API/Database Formatting Methods (Locale-Independent)
moment.fn.toApiDate = function() {
    // API dates should always be consistent regardless of locale - use DEFAULT_LOCALE
    if (currentMomentLocale === DEFAULT_LOCALE) {
        return this.format('YYYY-MM-DD');
    }
    // Create new moment instance with default locale for API formatting
    return this.clone().locale(DEFAULT_LOCALE).format('YYYY-MM-DD HH:mm:ss');
};

moment.fn.toApiDateTime = function() {
    // API datetimes should always be consistent regardless of locale - use DEFAULT_LOCALE
    if (currentMomentLocale === DEFAULT_LOCALE) {
        return this.format('YYYY-MM-DD HH:mm:ss');
    }
    // For non-default locales, format directly with YYYY-MM-DD HH:mm:ss pattern
    // This bypasses locale-specific formatting
    return this.format('YYYY-MM-DD HH:mm:ss');
};

// toLocaleString Integration Methods
moment.fn.toLocaleDateTime = function(locale = null) {
    const options = MomentExtensions.getLocaleStringOptions('shortDateTime', locale);
    return this.toDate().toLocaleString(locale || currentMomentLocale, options);
};

moment.fn.toLocaleDateTimeMedium = function(locale = null) {
    const options = MomentExtensions.getLocaleStringOptions('mediumDateTime', locale);
    return this.toDate().toLocaleString(locale || currentMomentLocale, options);
};

moment.fn.toLocaleDateTimeLong = function(locale = null) {
    const options = MomentExtensions.getLocaleStringOptions('longDateTime', locale);
    return this.toDate().toLocaleString(locale || currentMomentLocale, options);
};

// Safe Formatting Methods (Return Empty String for Invalid Dates)
moment.fn.toShortDateSafe = function(locale = null) {
    return this.isValid() ? this.toShortDate(locale) : '';
};

moment.fn.toMediumDateSafe = function(locale = null) {
    return this.isValid() ? this.toMediumDate(locale) : '';
};

moment.fn.toLongDateSafe = function(locale = null) {
    return this.isValid() ? this.toLongDate(locale) : '';
};

moment.fn.toShortTimeSafe = function(locale = null) {
    return this.isValid() ? this.toShortTime(locale) : '';
};

moment.fn.toShortDateTimeSafe = function(locale = null) {
    return this.isValid() ? this.toShortDateTime(locale) : '';
};

// Relative/Contextual Formatting Methods
moment.fn.toRelativeDate = function(locale = null) {
    if (this.isSame(moment(), 'day')) {
        return 'Today';
    }
    return this.toMediumDate(locale);
};

// Additional "Today" variants for specific use cases
moment.fn.toDateWithToday = function(locale = null) {
    if (this.isSame(moment(), 'day')) {
        return 'Today';
    }
    const currentYear = moment().year();

    // Return date without year if it's the current year
    if (this.year() === currentYear) {
        const dateOnlyFormat = locale === 'en-GB' || currentMomentLocale === 'en-GB'
            ? 'D MMM'  // e.g., "15 Jan"
            : 'MMM D'; // e.g., "Jan 15"
        return this.format(dateOnlyFormat);
    }

    // Return date with year if it's a different year
    return this.toMediumDate(locale);
};

moment.fn.toDateLongWithToday = function(locale = null) {
    if (this.isSame(moment(), 'day')) {
        return 'Today';
    }
    return this.toLongDate(locale);
};

moment.fn.toShortTimeWithNow = function (locale = null) {
    const now = moment();
    const diffMinutes = Math.floor((now.unix() - this.unix()) / 60);

    if (diffMinutes < 1) {
        return "Now";
    }

    return this.toShortTime(locale);
};


// Export both the class and configured moment
export { MomentExtensions, moment, DEFAULT_LOCALE };
